import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';
import { OnboardingModal } from '../../components/OnboardingModal';
import { ScreenPlate } from '../../components/ScreenPlate';
import { useAccountStore } from '../../stores/account';
import { useMatchesStore, MATCHES_CAP } from '../../stores/matches';
import { registerSlot, useTransitionActive } from '../../stores/transition';
import { useMotionTier, type MotionTier } from '../../components/useMotionTier';
import { useLayoutMode } from '../../components/useLayoutMode';
import { EDGES } from '../../theme/tokens';
import { duration, ease, stagger } from '../../theme/motion';
import { getMyRank, type MyRank } from '../../net/asyncClient';

interface MenuEntry {
  path: string;
  label: string;
  flavor: string;
  tone: 'cholo' | 'emo' | 'sodium' | 'tecate';
  /** Fallback text glyph, still used if the icon asset fails to load. */
  glyph: string;
  /** Die-cut prop icon under the public base (tools/screen-dressing-batch.py, lane 2).
      Optional: the codex entries have no prop drawn for them yet and fall back
      to the text glyph rather than requesting a 404. */
  icon?: string;
}

// Each nav icon is a culturally specific object rather than a generic UI symbol — a boombox, an
// engraved name plaque, a paletero cart, a hydraulic switch box. The icon set is itself a piece of
// worldbuilding, which is why these replaced the literal "▶ ✕ $ ⚙" characters that shipped here
// as placeholders.
const entries: MenuEntry[] = [
  { path: '/play', label: 'Play', flavor: '8-player free-for-all', tone: 'sodium', glyph: '▶', icon: 'icons/nav/play.webp' },
  { path: '/profile', label: 'Profile', flavor: 'crew, record, cosmetics', tone: 'cholo', glyph: '✕', icon: 'icons/nav/profile.webp' },
  { path: '/shop', label: 'Shop', flavor: 'card backs · emotes · trails', tone: 'tecate', glyph: '$', icon: 'icons/nav/shop.webp' },
  { path: '/settings', label: 'Settings', flavor: 'audio · language · controls', tone: 'emo', glyph: '⚙', icon: 'icons/nav/settings.webp' },
  // Codex — the two "read the game" screens. Nothing in the product let a
  // player study the roster or the pool outside the few seconds a draft or a
  // shop gives them.
  { path: '/heroes', label: 'Heroes', flavor: 'every headliner · powers', tone: 'cholo', glyph: '☰' },
  { path: '/minions', label: 'Minions', flavor: 'the whole card pool', tone: 'tecate', glyph: '❖' },
];

const toneClass: Record<MenuEntry['tone'], { text: string; glyphBg: string; font: string }> = {
  cholo:  { text: 'text-cholo',  glyphBg: 'bg-cholo',  font: 'font-cholo' },
  emo:    { text: 'text-emo',    glyphBg: 'bg-emo',    font: 'font-emo' },
  sodium: { text: 'text-sodium', glyphBg: 'bg-sodium', font: 'font-cholo' },
  tecate: { text: 'text-tecate', glyphBg: 'bg-tecate', font: 'font-cholo' },
};

// How long after the tape clears before the tutorial is allowed to open. The
// modal used to slam over the top of the menu's own entrance on first visit —
// on the single most important transition in the product, a first-time player
// got a popup where the payoff was supposed to be.
const ONBOARDING_GRACE_MS = 500;

export function MenuPage() {
  const hasSeenOnboarding = useAccountStore((s) => s.hasSeenOnboarding);
  const setHasSeenOnboarding = useAccountStore((s) => s.setHasSeenOnboarding);
  const openMatches = useMatchesStore((s) => s.countOpen());
  const [myRank, setMyRank] = useState<MyRank | null>(null);

  const tapeActive = useTransitionActive();
  const tier = useMotionTier();
  const { mode } = useLayoutMode();
  const portrait = mode === 'phonePortrait';
  // Captured at mount: did the tape transport deliver us here? If so the tiles
  // thread out of the tape path instead of running their own unrelated
  // stagger, so the arrival reads as one continuous sequence.
  const [threaded] = useState(() => tapeActive);
  // Always gated, never seeded from `tapeActive`. Seeding it meant that when
  // the commit and the end of a run landed in the same tick (the watchdog
  // path), this page mounted with the tape already inactive and the tutorial
  // opened instantly — the exact behaviour the grace exists to prevent. The
  // half-second also gives an ordinary direct visit time to settle before a
  // modal takes the screen.
  const [onboardingReady, setOnboardingReady] = useState(false);

  useEffect(() => {
    if (tapeActive) return;
    const t = window.setTimeout(() => setOnboardingReady(true), ONBOARDING_GRACE_MS);
    return () => window.clearTimeout(t);
  }, [tapeActive]);
  // Lazy-load my rank on mount. No spinner / fallback rendering — the
  // pill just appears once the fetch resolves. If offline, nothing
  // renders; the menu still works.
  useEffect(() => {
    let cancelled = false;
    getMyRank().then((r) => { if (!cancelled) setMyRank(r); });
    return () => { cancelled = true; };
  }, []);
  // Show the tutorial on first arrival at /menu. Dismissal flips the flag
  // (persisted to localStorage), so reloading the page won't re-show it.
  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      {/* Same wall as /welcome, held much further back (0.78 vs 0.55): this screen
          stacks three link strips and a 2x2 tile grid over it, and the plate's job
          here is to give the asphalt some depth, not to be looked at. Framed low so
          the lit lower half of the wall sits behind the tiles. */}
      <ScreenPlate src="plates/parking_lot.webp" scrim={0.78} position="center 70%" />

      {/* Single sodium wash on the right — leaves the menu tiles uncluttered.
          It drifts rather than sitting still: the menu is where a player
          lingers, and a dead-static background made the screen feel like it
          had stopped the moment the transition ended. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-1/3 top-1/4 h-[60vw] w-[60vw] max-h-[760px] max-w-[760px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.18) 0%, rgba(255,138,61,0) 70%)',
        }}
        animate={
          tier === 'reduced' ? undefined : { x: [0, -14, 0], y: [0, 10, 0] }
        }
        transition={
          tier === 'reduced'
            ? undefined
            : { duration: 16, ease: ease.inOut, repeat: Infinity }
        }
      />

      {/* Portrait sizing is driven by MODE, not by `sm:`/`md:` width
          breakpoints. Those never fire on a 390px-wide phone, so the whole
          screen fell back to a mobile-first base that was authored for a
          LANDSCAPE phone (~390px tall) — tiny strips and 34px tiles crammed
          into the top third of an 844px screen with a void beneath them. */}
      <section
        className={`relative mx-auto flex h-full w-full max-w-5xl flex-col px-md md:gap-lg md:px-lg md:py-lg lg:max-w-6xl lg:gap-xl lg:py-xl ${
          portrait ? 'gap-sm py-sm' : 'gap-xs py-xs'
        }`}
      >
        <header className="flex items-center justify-between">
          {/* The tape transport's landing pad. `registerSlot` measures
              this wrapper so the travelling title knows where to touch down;
              the real mark then fades up underneath it as it arrives, so the
              hand-off never shows two copies of the wordmark at once. */}
          <motion.span
            ref={registerSlot('wordmark')}
            className="inline-flex"
            initial={threaded ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={
              threaded
                ? { delay: 0.62, duration: duration.hint, ease: ease.out }
                : { duration: 0 }
            }
          >
            <span className="md:hidden">
              <WordMark size="sm" />
            </span>
            <span className="hidden md:inline lg:hidden">
              <WordMark size="md" />
            </span>
            <span className="hidden lg:inline">
              <WordMark size="lg" />
            </span>
          </motion.span>
          <Link
            to="/welcome"
            className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← welcome
          </Link>
        </header>

        {/* Matches strip — only when at least one match is in flight.
            Sits above ladder so resuming an in-progress run is the most
            prominent menu action when relevant. */}
        {openMatches > 0 && (
          <Link
            to="/matches"
            className={`flex items-center justify-between rounded-md border bg-stucco1 px-sm transition-colors hover:bg-stucco2 md:px-md md:py-sm ${
            portrait ? 'min-h-[52px] py-sm' : 'py-[3px]'
          }`}
            style={{ borderColor: 'rgba(255,138,61,0.4)' }}
          >
            <div className="flex items-center gap-sm md:gap-md">
              <span className={`rounded-sm bg-sodium/20 px-xs py-[1px] font-data uppercase tracking-[0.25em] text-sodium md:text-[10px] ${
                portrait ? 'text-[11px]' : 'text-[9px]'
              }`}>
                matches
              </span>
              <span className={`font-data uppercase tracking-[0.2em] text-marker md:text-[13px] ${
                portrait ? 'text-[12px] leading-tight' : 'text-[11px]'
              }`}>
                {openMatches} in flight · resume run →
              </span>
            </div>
            <span className="font-data text-[10px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
              {openMatches}/{MATCHES_CAP}
            </span>
          </Link>
        )}

        {/* Friends strip — always visible. Tap → /friends to add, invite,
            or challenge. Phase 3 will surface unread-request count here. */}
        <Link
          to="/friends"
          className={`flex items-center justify-between rounded-md border bg-stucco1 px-sm transition-colors hover:bg-stucco2 md:px-md md:py-sm ${
            portrait ? 'min-h-[52px] py-sm' : 'py-[3px]'
          }`}
          style={{ borderColor: EDGES.soft }}
        >
          <div className="flex items-center gap-sm md:gap-md">
            <span className={`rounded-sm bg-cholo/20 px-xs py-[1px] font-data uppercase tracking-[0.25em] text-cholo md:text-[10px] ${
                portrait ? 'text-[11px]' : 'text-[9px]'
              }`}>
              friends
            </span>
            <span className={`font-data uppercase tracking-[0.2em] text-marker md:text-[13px] ${
                portrait ? 'text-[12px] leading-tight' : 'text-[11px]'
              }`}>
              your crew · add · invite
            </span>
          </div>
          <span aria-hidden className={`font-data text-graphite md:text-[18px] ${portrait ? 'text-[18px]' : 'text-[14px]'}`}>›</span>
        </Link>

        {/* What we changed — the pitch's other half, which is otherwise
            invisible. A player who knows Battlegrounds will not notice a
            fixed frustration unless something says so, and one who does not
            know it cannot notice at all. Sits below the ladder because it is
            a read, not a thing to do. */}
        <Link
          to="/changes"
          className={`flex items-center justify-between rounded-md border bg-stucco1 px-sm transition-colors hover:bg-stucco2 md:px-md md:py-sm ${
            portrait ? 'min-h-[52px] py-sm' : 'py-[3px]'
          }`}
          style={{ borderColor: EDGES.soft }}
        >
          <div className="flex items-center gap-sm md:gap-md">
            <span className={`rounded-sm bg-sodium/20 px-xs py-[1px] font-data uppercase tracking-[0.25em] text-sodium md:text-[10px] ${
                portrait ? 'text-[11px]' : 'text-[9px]'
              }`}>
              changes
            </span>
            <span className={`font-data uppercase tracking-[0.2em] text-marker md:text-[13px] ${
                portrait ? 'text-[12px] leading-tight' : 'text-[11px]'
              }`}>
              what we fixed about battlegrounds
            </span>
          </div>
          <span aria-hidden className={`font-data text-graphite md:text-[18px] ${portrait ? 'text-[18px]' : 'text-[14px]'}`}>›</span>
        </Link>

        {/* Ladder strip — single tap-target above the nav grid. Doubles
            as a teaser when the player has no rank yet (encourages a
            first async match to "join the board"). */}
        <Link
          to="/ladder"
          className={`flex items-center justify-between rounded-md border bg-stucco1 px-sm transition-colors hover:bg-stucco2 md:px-md md:py-sm ${
            portrait ? 'min-h-[52px] py-sm' : 'py-[3px]'
          }`}
          style={{ borderColor: EDGES.soft }}
        >
          <div className="flex items-center gap-sm md:gap-md">
            <span className={`rounded-sm bg-sodium/20 px-xs py-[1px] font-data uppercase tracking-[0.25em] text-sodium md:text-[10px] ${
                portrait ? 'text-[11px]' : 'text-[9px]'
              }`}>
              ladder
            </span>
            <span className={`font-data uppercase tracking-[0.2em] text-marker md:text-[13px] ${
                portrait ? 'text-[12px] leading-tight' : 'text-[11px]'
              }`}>
              {myRank === null
                ? 'top players · async pvp'
                : myRank.rank === null
                  ? `unranked · play to enter (${myRank.rating} elo)`
                  : `rank #${myRank.rank} · ${myRank.rating} elo · ${myRank.gamesPlayed} games`}
            </span>
          </div>
          <span aria-hidden className={`font-data text-graphite md:text-[18px] ${portrait ? 'text-[18px]' : 'text-[14px]'}`}>›</span>
        </Link>

        {/* 2×2 in landscape (no height to spare) and on wider screens.
            Portrait has the height for full-width rows, which gives each
            tile's flavor line room to breathe instead of wrapping. */}
        <nav
          className={`grid md:gap-md lg:gap-lg ${
            portrait
              // flex-1 + auto-rows-fr: the four tiles share whatever height
              // the strips and footer leave, instead of sitting at a fixed
              // 34px and leaving ~500px of dead screen underneath them.
              ? 'min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-sm'
              : 'grid-cols-2 gap-xs'
          }`}
        >
          {entries.map((entry, i) => (
            <MenuTile
              key={entry.path}
              entry={entry}
              index={i}
              threaded={threaded}
              tier={tier}
              portrait={portrait}
            />
          ))}
        </nav>

        <footer className="mt-auto text-center font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[12px] lg:text-[13px]">
          v2 · {new Date().getFullYear()} · serifeus studio
        </footer>
      </section>

      <OnboardingModal
        open={!hasSeenOnboarding && onboardingReady}
        onDismiss={() => setHasSeenOnboarding(true)}
      />
    </main>
  );
}

function MenuTile({
  entry,
  index,
  threaded,
  tier,
  portrait,
}: {
  entry: MenuEntry;
  index: number;
  threaded: boolean;
  tier: MotionTier;
  /** Portrait phones size from MODE, not width breakpoints — see the
      section comment in MenuPage. */
  portrait: boolean;
}) {
  const tone = toneClass[entry.tone];
  const reduced = tier === 'reduced';
  const [iconFailed, setIconFailed] = useState(false);

  // Two different arrivals for the same tile.
  //
  // `threaded` — the tape delivered us. Tiles unspool out of the horizontal
  // tape path: the 2×2 grid's top row rises from the centreline and the bottom
  // row drops from it, each compressed horizontally then pulling to width. The
  // sequence continues rather than restarting.
  //
  // Otherwise — an ordinary navigation. Keep the original left-slide.
  const row = index < 2 ? 0 : 1;
  const initial = reduced
    ? { opacity: 0 }
    : threaded
      ? { opacity: 0, scaleX: 0.86, y: row === 0 ? 16 : -16 }
      : { opacity: 0, x: -16 };

  const delay = reduced ? 0 : (threaded ? 0.66 : 0) + stagger.base * index;

  return (
    <motion.div
      initial={initial}
      animate={{ opacity: 1, x: 0, y: 0, scaleX: 1 }}
      transition={{
        delay,
        duration: reduced ? duration.tap : duration.enter,
        ease: threaded ? ease.transport : ease.out,
      }}
      whileHover={reduced ? undefined : { x: 4 }}
      whileTap={{ scale: 0.98 }}
      className={portrait ? 'min-h-0' : undefined}
    >
      <Link
        to={entry.path}
        className={`group flex items-center rounded-md border bg-stucco1 transition-colors hover:bg-stucco2 sm:min-h-[40px] sm:gap-sm sm:px-sm sm:py-[3px] md:min-h-[64px] md:gap-md md:px-md md:py-md lg:min-h-[88px] lg:gap-lg lg:px-lg lg:py-lg ${
          portrait
            ? 'h-full min-h-[72px] gap-md px-md py-sm'
            : 'min-h-[34px] gap-xs px-xs py-[2px] [@media(max-height:500px)]:min-h-[30px]'
        }`}
        style={{ borderColor: EDGES.soft }}
      >
        {/* Icon chip — faction-tinted tile, sharp corner, with the die-cut prop
            sitting on top of it. The tint still carries the colour coding; the
            prop is what makes the tile feel authored. Falls back to the old text
            glyph if the asset 404s, so a missing file degrades to the previous
            look rather than an empty chip. */}
        <span
          className={`flex shrink-0 items-center justify-center rounded-sm ${tone.glyphBg} text-sharpie font-data sm:h-9 sm:w-9 sm:text-[14px] md:h-12 md:w-12 md:text-[20px] lg:h-14 lg:w-14 lg:text-[24px] ${
            portrait
              ? 'h-14 w-14 text-[24px]'
              : 'h-7 w-7 text-[12px] [@media(max-height:500px)]:h-6 [@media(max-height:500px)]:w-6 [@media(max-height:500px)]:text-[11px]'
          }`}
          aria-hidden
        >
          {iconFailed || !entry.icon ? (
            entry.glyph
          ) : (
            <img
              src={`${import.meta.env.BASE_URL}${entry.icon}`}
              alt=""
              onError={() => setIconFailed(true)}
              className="h-[85%] w-[85%] object-contain"
              decoding="async"
            />
          )}
        </span>
        <span className="flex flex-1 flex-col leading-tight">
          <span className={`sm:text-[18px] md:text-[26px] lg:text-[36px] ${tone.font} ${tone.text} ${
              portrait ? 'text-[26px]' : 'text-[15px] [@media(max-height:500px)]:text-[14px]'
            }`}>
            {entry.label}
          </span>
          <span className={`font-data uppercase tracking-[0.2em] text-marker sm:text-[9px] md:text-[11px] lg:text-[13px] ${
              portrait ? 'text-[11px]' : 'text-[8px] [@media(max-height:500px)]:hidden'
            }`}>
            {entry.flavor}
          </span>
        </span>
        <span
          className={`font-data text-graphite transition-colors group-hover:text-sodium sm:text-[14px] md:text-[18px] lg:text-[22px] ${
            portrait ? 'text-[20px]' : 'text-[12px]'
          }`}
          aria-hidden
        >
          ›
        </span>
      </Link>
    </motion.div>
  );
}
